/**
 * Detects the in-app browsers that cannot save a downloaded file.
 *
 * On iOS, a WKWebView only downloads a `Content-Disposition: attachment`
 * response if the host app implements `WKDownloadDelegate`. Instagram, Facebook
 * and X do not, so tapping Download runs every line of our code successfully —
 * the API returns 200, the signed URL is valid, the ZIP is in S3 — and the file
 * silently goes nowhere. There is no JS workaround; the page has to be reopened
 * in Safari.
 *
 * This is not a rare case. Over the 30 days to 2026-09-12, Instagram-on-iOS was
 * the single largest source of album-download traffic in prd (180 requests, 45
 * users), well ahead of real Mobile Safari, because artists send buyers there
 * from link-in-bio. Those sessions averaged 3.2 download presses against ~1.2
 * everywhere else — people tapping again because nothing happened.
 *
 * Android is deliberately excluded: its WebView hands downloads to the system
 * downloader, and Instagram-on-Android averages 1.0 presses per session, so a
 * warning there would be a false alarm.
 */

export type InAppBrowserApp = "Instagram" | "Facebook" | "X";

const IOS_DEVICE = /iPad|iPhone|iPod/;

// iPadOS reports itself as a Mac unless the embedder opts out, so an iPad in
// the Instagram or Facebook WebView can arrive as "Macintosh; Intel Mac OS X"
// with no iPad token. The `Mobile` build token is what separates it from a
// real Mac: desktop Safari and Chrome never send it, and a genuine macOS
// session would additionally have to carry an Instagram or FBAN token to be
// misread, which does not happen — there are no Mac builds of those apps.
const MACINTOSH = /Macintosh/;
const MOBILE_TOKEN = /Mobile/;

function isIosDevice(userAgent: string): boolean {
  if (IOS_DEVICE.test(userAgent)) return true;
  return MACINTOSH.test(userAgent) && MOBILE_TOKEN.test(userAgent);
}

// Order matters: the Instagram and Facebook apps share Meta's WebView, and
// Instagram's user agent carries the FBAV/FBAN tokens too, so it must be
// tested first or every Instagram user is told they are in Facebook.
const IN_APP_SIGNATURES: ReadonlyArray<[InAppBrowserApp, RegExp]> = [
  ["Instagram", /Instagram/],
  ["Facebook", /FBAN|FBAV|FB_IAB/],
  ["X", /Twitter/],
];

/**
 * Returns the in-app browser the page is running inside, or `null` when the
 * browser can download normally. Callers should treat `null` as "no warning
 * needed" rather than "definitely a real browser" — user agents lie, and this
 * only has to catch the three apps that account for the failures we see.
 */
export function getInAppBrowserApp(
  userAgent: string | undefined = typeof navigator === "undefined"
    ? undefined
    : navigator.userAgent,
): InAppBrowserApp | null {
  if (!userAgent) return null;
  if (!isIosDevice(userAgent)) return null;

  const match = IN_APP_SIGNATURES.find(([, signature]) =>
    signature.test(userAgent),
  );

  return match ? match[0] : null;
}

/**
 * How to escape each app's browser. The wording differs per app, and sending
 * someone hunting for a menu item that is not there is worse than saying
 * nothing, so anything we are unsure of falls back to the generic phrasing.
 */
export function getOpenInBrowserHint(app: InAppBrowserApp): string {
  switch (app) {
    case "Instagram":
    case "Facebook":
      return "tap ••• in the corner, then Open in external browser";
    case "X":
      return "use the browser menu to open it in Safari";
  }
}
